import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import AskQuestionPanel from "../AskQuestions/AskQuestions";
import { QuestionItem } from "../QuestionItem/QuestionItem";
import {
  Box,
  Container,
  Header,
  Icon,
  Text,
  VStack,
  HStack,
  Pressable,
} from "@repo/ui";
import { AnalyticsEvents, useAnalytics } from "@repo/analytics";
import {
  AVATAR_ITEMS,
  formatTimestampWithOrdinalInTimeZone,
  useIsMobile,
  useIsTablet,
} from "@repo/utils";
import { useTheme } from "@repo/tokens";
import { useIsMweb } from "../../../hooks/useIsMweb";
import { spacingRem } from "../../../lib/spacingRem";
import { useDoubtsAccessExhaustion } from "../../../lib/doubtsAccess";
import Filter from "../Filter/Filter";
import {
  emptyDoubtsFilterState,
  type DoubtsFilterState,
} from "../Filter/filterTypes";
import {
  localDateEndUtcSeconds,
  localDateStartUtcSeconds,
} from "@repo/doubts-utils";
import type { QuestionItemProps } from "../QuestionItem/types";
import { readDoubtBuddyMessageId } from "../../../lib/createDoubtResponse";
import noBookmarkFoundSrc from "../../../assets/instructions/no-bookmark-found.svg";
import emptyDoubtSrc from "../../../assets/instructions/empty-doubt.svg";
import { useDoubtData } from "../../../lib/DoubtContext";
import { useDoubtErrorToast } from "../../../lib/doubtErrorToast";
import {
  postDc1UserGetDoubtsv2,
  getDc1UsersUseridBookmarksv2,
  useGetDc1AvailableFreeDoubts,
  usePostDc1DoubtBookmark,
  useDeleteDc1DoubtBookmark,
} from "@oneapp/data";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
const DOUBTS_PAGE_SIZE = 10;

const readRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
};

const isInitialFreeDoubtsCreditResponse = (payload: unknown): boolean => {
  const raw = readRecord(payload);
  if (!raw) return false;
  const nested = readRecord(raw.data);
  const source = nested ?? raw;

  const hasCreditedMessage =
    typeof source.creditedFreeDoubtsMessage === "string" &&
    source.creditedFreeDoubtsMessage.trim().length > 0;

  const availableFreeDoubts = Number(source.availableFreeDoubts);
  const hasAvailable =
    Number.isFinite(availableFreeDoubts) && availableFreeDoubts >= 0;

  return hasCreditedMessage && !hasAvailable;
};

function readTotalCountFromResponse(
  response: Record<string, unknown> | undefined,
): number | undefined {
  if (!response) return undefined;
  const data = response.data as Record<string, unknown> | undefined;
  for (const source of [response, data]) {
    if (!source) continue;
    for (const key of ["count", "total", "totalCount"] as const) {
      const v = (source as Record<string, unknown>)[key];
      if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
      if (typeof v === "string" && v.length > 0) {
        const n = Number(v);
        if (Number.isFinite(n) && n >= 0) return n;
      }
    }
  }
  return undefined;
}

// --- Helpers ported from mobile ExistingUser ---

const formatCreatedTime = (
  value: unknown,
  studentTimeZone?: string | null,
): string | undefined => {
  if (value == null) return undefined;

  const formattedDate = formatTimestampWithOrdinalInTimeZone(
    value as Parameters<typeof formatTimestampWithOrdinalInTimeZone>[0],
    studentTimeZone,
    { locale: "en-US", fallback: "" },
  );

  return formattedDate || undefined;
};
function parseOptionalPositiveInt(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

type RawQuestionItem = {
  id?: unknown;
  doubtId?: unknown;
  questionId?: unknown;
  question_id?: unknown;
  doubtText?: unknown;
  text?: unknown;
  questionText?: unknown;
  question?: unknown;
  createdTime?: unknown;
  created_time?: unknown;
  createTime?: unknown;
  createdAt?: unknown;
  created_at?: unknown;
  createdOn?: unknown;
  date?: unknown;
  timeStamp?: unknown;
  status?: unknown;
  state?: unknown;
  doubtCardStatus?: unknown;
  subjectName?: unknown;
  subject?: unknown;
  isBookmarked?: unknown;
  image?: unknown;
  imageUrl?: unknown;
  doubtImage?: unknown;
  isImageDoubt?: unknown;
  subjectId?: unknown;
  qbId?: unknown;
  qb_id?: unknown;
  questionBankId?: unknown;
  doubtBuddyMessageId?: unknown;
  messageId?: unknown;
};

function extractRawListFromResponse(
  response: Record<string, unknown> | undefined,
): RawQuestionItem[] | undefined {
  if (!response) return undefined;
  const listCandidates = [
    response?.questions,
    response?.doubts,
    (response?.data as Record<string, unknown> | undefined)?.questions,
    response?.data,
    response?.result,
    response?.items,
    response?.records,
  ];
  return listCandidates.find(Array.isArray) as RawQuestionItem[] | undefined;
}

function mapRawListToQuestionItems(
  rawList: RawQuestionItem[],
  options?: { forceBookmarked?: boolean; studentTimeZone?: string | null },
): QuestionItemProps[] {
  const forceBookmarked = options?.forceBookmarked === true;
  return rawList.map((item: RawQuestionItem, index: number) => {
    const rawText =
      item.doubtText ?? item.text ?? item.questionText ?? item.question;
    const text = typeof rawText === "string" ? rawText.trim() : undefined;

    const rawCreatedTime =
      item.createdTime ??
      item.created_time ??
      item.createTime ??
      item.createdAt ??
      item.created_at ??
      item.createdOn ??
      item.date ??
      item.timeStamp;

    const formattedDate = formatCreatedTime(
      rawCreatedTime,
      options?.studentTimeZone,
    );

    const rawDoubtId = item.doubtId ?? item.questionId ?? item.question_id;
    const parsedDoubtId = Number(rawDoubtId);
    const doubtId =
      Number.isFinite(parsedDoubtId) && parsedDoubtId > 0
        ? parsedDoubtId
        : undefined;

    const rawId = item.id;
    const parsedId = Number(rawId);
    const id =
      Number.isFinite(parsedId) && parsedId > 0
        ? parsedId
        : (doubtId ?? index + 1);

    const rawSubject = item.subject;
    const subjectLabel =
      item.subjectName != null
        ? String(item.subjectName)
        : typeof rawSubject === "string"
          ? rawSubject
          : rawSubject && typeof rawSubject === "object" && "name" in rawSubject
            ? String((rawSubject as { name?: unknown }).name)
            : "General";

    let subjectId: number | undefined;
    const rawSubjectId = item.subjectId;
    if (
      typeof rawSubjectId === "number" &&
      Number.isFinite(rawSubjectId) &&
      rawSubjectId > 0
    ) {
      subjectId = rawSubjectId;
    } else if (
      rawSubject &&
      typeof rawSubject === "object" &&
      "id" in rawSubject
    ) {
      const sid = Number((rawSubject as { id?: unknown }).id);
      if (Number.isFinite(sid) && sid > 0) {
        subjectId = sid;
      }
    }

    const qbId = parseOptionalPositiveInt(
      item.qbId ?? item.qb_id ?? item.questionBankId,
    );

    const doubtBuddyMessageId = readDoubtBuddyMessageId(item as unknown);

    const isBookmarked = forceBookmarked || Boolean(item.isBookmarked);

    const rawStatus = String(item.status ?? item.state ?? "Pending");
    const rawCardStatus =
      item.doubtCardStatus != null ? String(item.doubtCardStatus) : rawStatus;

    const rawImage = item.doubtImage ?? item.image ?? item.imageUrl;
    const image =
      typeof rawImage === "string" && rawImage.trim().length > 0
        ? rawImage
        : undefined;
    const isImageDoubt = Boolean(item.isImageDoubt) && Boolean(image);

    return {
      id,
      doubtId,
      subjectId,
      qbId,
      status: rawStatus,
      doubtCardStatus: rawCardStatus,
      subject: subjectLabel,
      isBookmarked,
      text,
      isImageDoubt,
      date:
        formattedDate ??
        (rawCreatedTime != null ? String(rawCreatedTime) : "N/A"),
      image,
      ...(doubtBuddyMessageId != null ? { doubtBuddyMessageId } : {}),
    };
  });
}

/** True when the user has applied any filter criterion (not the default "show all"). */
function isDoubtsFilterActive(f: DoubtsFilterState): boolean {
  return (
    f.subjects.length > 0 ||
    f.status.length > 0 ||
    Boolean(f.startDate) ||
    Boolean(f.endDate)
  );
}

// --- Reusable Sub-components ---

type LeftPanelProps = {
  questionsForList: QuestionItemProps[];
  showFilterEmptyIllustration: boolean;
  showBookmarksEmpty: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  showBookmarks: boolean;
  onToggleBookmarks: () => void;
  onOpenFilter: () => void;
  onBookmarkToggle: (question: QuestionItemProps) => void;
  onQuestionPress: (question: QuestionItemProps) => void;
  variant?: "desktop" | "mweb";
  appliedFilterCount?: number;
  /** Doubt id whose Get answers chat is open — list row highlight. */
  highlightedDoubtId?: number | null;
  emptyStateHeight?: number;
  /** Sentinel ref placed at the bottom of the list to drive infinite scroll. */
  loadMoreSentinelRef?: React.RefObject<HTMLDivElement | null>;
  /** True while the next page of `getDoubtsv2` / `bookmarksv2` is being fetched. */
  isFetchingMore?: boolean;
  /** True when there are more pages available to load. */
  hasMore?: boolean;
};

const LeftPanel = ({
  questionsForList,
  showFilterEmptyIllustration,
  showBookmarksEmpty,
  isLoading,
  isError,
  onRetry,
  showBookmarks,
  onToggleBookmarks,
  onOpenFilter,
  onBookmarkToggle,
  onQuestionPress,
  variant = "desktop",
  appliedFilterCount = 0,
  highlightedDoubtId = null,
  emptyStateHeight,
  loadMoreSentinelRef,
  isFetchingMore = false,
  hasMore = false,
}: LeftPanelProps) => {
  const { t } = useTranslation("doubts");
  const { theme } = useTheme();
  const isMwebPanel = variant === "mweb";

  let listContent: React.ReactNode;

  if (isLoading) {
    listContent = (
      <div
        className="left-column-loading"
        aria-label={t("questions_loading_a11y")}
      >
        <div className="left-column-loading-dots">
          <span className="left-column-loading-dot left-column-loading-dot--1" />
          <span className="left-column-loading-dot left-column-loading-dot--2" />
          <span className="left-column-loading-dot left-column-loading-dot--3" />
        </div>
      </div>
    );
  } else if (isError) {
    listContent = (
      <div className="left-column-error">
        <p className="left-column-error-text">{t("questions_load_error")}</p>
        <button
          type="button"
          className="left-column-retry-btn"
          onClick={onRetry}
        >
          {t("retry")}
        </button>
      </div>
    );
  } else if (showFilterEmptyIllustration) {
    listContent = (
      <div className="left-column-bookmark-empty">
        <img
          src={emptyDoubtSrc}
          alt={t("no_questions_yet")}
          width={354}
          height={188}
        />
      </div>
    );
  } else if (showBookmarksEmpty) {
    listContent = (
      <div className="left-column-bookmark-empty">
        <img
          src={noBookmarkFoundSrc}
          alt={t("no_bookmarks_filtered_a11y")}
          width={354}
          height={188}
        />
      </div>
    );
  } else if (questionsForList.length === 0) {
    listContent = (
      <div className="left-column-bookmark-empty">
        <img
          src={emptyDoubtSrc}
          alt={t("no_questions_yet")}
          width={354}
          height={188}
        />
      </div>
    );
  } else {
    listContent = (
      <>
        {questionsForList.map((q) => (
          <QuestionItem
            key={q.doubtId ?? q.id}
            question={q}
            isActiveChat={
              highlightedDoubtId != null &&
              q.doubtId != null &&
              q.doubtId === highlightedDoubtId
            }
            onBookmarkPress={() => onBookmarkToggle(q)}
            onQuestionPress={
              q.doubtId != null && q.doubtId > 0
                ? () => onQuestionPress(q)
                : undefined
            }
          />
        ))}
        {(hasMore || isFetchingMore) && (
          <div
            ref={loadMoreSentinelRef}
            className="left-column-load-more"
            aria-hidden={!isFetchingMore}
          >
            {isFetchingMore ? (
              <div
                className="left-column-loading-dots"
                aria-label="Loading more questions"
              >
                <span className="left-column-loading-dot left-column-loading-dot--1" />
                <span className="left-column-loading-dot left-column-loading-dot--2" />
                <span className="left-column-loading-dot left-column-loading-dot--3" />
              </div>
            ) : null}
          </div>
        )}
      </>
    );
  }

  const listIsEmptyVisual =
    showFilterEmptyIllustration ||
    showBookmarksEmpty ||
    questionsForList.length === 0;

  const headerTitle = t("my_questions");

  const titleRow = (
    <HStack
      className="left-column-header"
      justify="space-between"
      align="center"
    >
      <Box
        as="h3"
        style={{
          ...theme.typography.title.large.regular,
          fontWeight: "500",
        }}
      >
        {headerTitle}
      </Box>
      <Pressable
        onPress={onToggleBookmarks}
        aria-label={t("bookmarked_questions_title")}
        style={{ display: "inline-flex" }}
      >
        <Icon
          name={showBookmarks ? "selected_bookmark" : "bookmarkIconDoubts"}
          width={25}
          height={25}
          color={theme.colors.primary[40]}
        />
      </Pressable>
    </HStack>
  );

  const filterActive = appliedFilterCount > 0;

  const mwebFilterButton = (
    <button
      type="button"
      className={
        filterActive
          ? "doubts-mweb-filter-row doubts-mweb-filter-row--active"
          : "doubts-mweb-filter-row"
      }
      onClick={onOpenFilter}
      aria-label={
        filterActive ? `${t("filter")} (${appliedFilterCount})` : t("filter")
      }
    >
      <span className="doubts-mweb-filter-row-inner">
        <Icon
          name="Filter"
          width={15}
          height={15}
          color={theme.colors.primary[40]}
        />
        {t("filter")}
      </span>
      {filterActive ? (
        <span className="doubts-mweb-filter-badge">{appliedFilterCount}</span>
      ) : (
        <span aria-hidden />
      )}
    </button>
  );

  const inner = (
    <>
      {isMwebPanel ? (
        <div className="doubts-mweb-sticky-list-controls">
          <div className="left-column-controls">
            {titleRow}
            {mwebFilterButton}
          </div>
        </div>
      ) : (
        <div className="left-column-controls">
          {titleRow}
          <button
            type="button"
            className={
              filterActive
                ? "doubts-desktop-filter-btn doubts-desktop-filter-btn--active"
                : "doubts-desktop-filter-btn"
            }
            onClick={onOpenFilter}
            aria-label={
              filterActive
                ? `${t("filter")} (${appliedFilterCount})`
                : t("filter")
            }
          >
            <span className="doubts-desktop-filter-btn-inner">
              <Icon
                name="Filter"
                width={15}
                height={15}
                color={theme.colors.primary[40]}
              />
              {t("filter")}
            </span>
            {filterActive ? (
              <span className="doubts-filter-count-badge">
                {appliedFilterCount}
              </span>
            ) : (
              <span aria-hidden />
            )}
          </button>
        </div>
      )}
      <VStack
        className={
          listIsEmptyVisual
            ? "left-column-list left-column-list--empty-state"
            : "left-column-list"
        }
        gap="lg"
      >
        {listContent}
      </VStack>
    </>
  );

  if (isMwebPanel) {
    return <div className="doubts-mweb-left-panel">{inner}</div>;
  }

  const constrainHeight = listIsEmptyVisual && emptyStateHeight;

  return (
    <Container
      style={{
        backgroundColor:
          "color-mix(in srgb, var(--colors-primary-95) 50%, transparent)",
        padding: spacingRem(theme.spacing.lg),
        borderRadius: theme.radii.lg,
        flex: isMwebPanel ? 1 : "434 1 0",
        minHeight: 0,
        height: constrainHeight ? emptyStateHeight : "auto",
        display: "flex",
        flexDirection: "column",
        gap: spacingRem(theme.spacing.lg),
        marginBottom: isMwebPanel ? spacingRem(theme.spacing.lg) : 0,
        alignSelf: constrainHeight ? "flex-start" : "stretch",
        overflow: constrainHeight ? "hidden" : undefined,
        minWidth: 0,
      }}
    >
      {inner}
    </Container>
  );
};

const RightPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      as="section"
      className="right-column"
      style={{
        flex: "791 1 0",
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
};

const DoubtsDashboard = () => {
  const { t } = useTranslation("doubts");
  const { track } = useAnalytics();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isNarrowLayout = isMobile || isTablet;
  const { theme } = useTheme();
  const navigate = useNavigate();
  const session = useDoubtData();
  const isMweb = useIsMweb();
  const { isExhausted: accessExhausted } = useDoubtsAccessExhaustion(session);
  const { showSomethingWentWrong } = useDoubtErrorToast();

  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [showBookmarks, setShowBookmarks] = React.useState(false);
  const [appliedFilter, setAppliedFilter] = React.useState<DoubtsFilterState>(
    () => emptyDoubtsFilterState(),
  );
  /** In-flight optimistic bookmark toggles keyed by doubtId (Android bookmarkOverrides). */
  const [bookmarkOverridesByDoubtId, setBookmarkOverridesByDoubtId] =
    React.useState<Record<number, boolean>>({});
  /** Last successful bookmark state per doubtId (avoids refetch + list loading flash after toggle). */
  const [bookmarkDisplayByDoubtId, setBookmarkDisplayByDoubtId] =
    React.useState<Record<number, boolean>>({});

  const userId = session.uamId;
  const dcNumericTenantId = session.dcNumericTenantId;
  const tenantId = session.tenantId;
  const gradeId = session.gradeId;

  const {
    data: dashboardFreeDoubtsData,
    isFetching: isDashboardFreeDoubtsFetching,
    refetch: refetchDashboardFreeDoubts,
  } = useGetDc1AvailableFreeDoubts(
    {
      userId: String(userId ?? ""),
      tenantId: dcNumericTenantId ?? "",
    },
    {
      request: { clientName: "doubtsAi" },
      query: { enabled: !!userId && !!dcNumericTenantId },
    },
  );
  const [didRetryDashboardFreeDoubts, setDidRetryDashboardFreeDoubts] =
    React.useState(false);

  React.useEffect(() => {
    setDidRetryDashboardFreeDoubts(false);
  }, [userId, dcNumericTenantId]);

  React.useEffect(() => {
    if (!userId || !dcNumericTenantId) return;
    if (didRetryDashboardFreeDoubts) return;
    if (isDashboardFreeDoubtsFetching) return;
    if (!isInitialFreeDoubtsCreditResponse(dashboardFreeDoubtsData)) return;

    setDidRetryDashboardFreeDoubts(true);
    void refetchDashboardFreeDoubts();
  }, [
    userId,
    dcNumericTenantId,
    didRetryDashboardFreeDoubts,
    isDashboardFreeDoubtsFetching,
    dashboardFreeDoubtsData,
    refetchDashboardFreeDoubts,
  ]);

  const startTimestamp = localDateStartUtcSeconds(
    appliedFilter.startDate,
    session.studentTimeZone,
  );
  const endTimestamp = localDateEndUtcSeconds(
    appliedFilter.endDate,
    session.studentTimeZone,
  );

  const subjectIds = React.useMemo(
    () => appliedFilter.subjects.map((s) => s.subjectId),
    [appliedFilter.subjects],
  );

  const statusFilters = React.useMemo(
    () => (appliedFilter.status.length > 0 ? appliedFilter.status : []),
    [appliedFilter.status],
  );

  const [doubtsRefreshTick, setDoubtsRefreshTick] = React.useState(0);
  const [activeChatDoubtId, setActiveChatDoubtId] = React.useState<
    number | null
  >(null);
  const [bookmarksRefreshTick, setBookmarksRefreshTick] = React.useState(0);
  const [statusOverridesByDoubtId, setStatusOverridesByDoubtId] =
    React.useState<
      Record<number, Pick<QuestionItemProps, "status" | "doubtCardStatus">>
    >({});

  const {
    data: doubtsInfiniteData,
    isPending: isDoubtsPending,
    isError: isDoubtsError,
    isFetchingNextPage: isFetchingNextDoubts,
    hasNextPage: hasNextDoubtsPage,
    fetchNextPage: fetchNextDoubts,
  } = useInfiniteQuery({
    queryKey: [
      "postDc1UserGetDoubtsv2",
      "infinite",
      userId,
      tenantId,
      gradeId,
      subjectIds,
      statusFilters,
      startTimestamp ?? null,
      endTimestamp ?? null,
      doubtsRefreshTick,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      if (!userId) {
        return Promise.resolve({} as Record<string, unknown>);
      }
      return postDc1UserGetDoubtsv2(
        {
          userId,
          tenantId,
          ...(gradeId != null ? { gradeId } : {}),
          page: pageParam,
          subjectIds,
          status: statusFilters,
          from_utc: startTimestamp ?? null,
          to_utc: endTimestamp ?? null,
        } as Record<string, unknown>,
        { clientName: "doubtsAi" },
      ) as Promise<Record<string, unknown>>;
    },
    getNextPageParam: (lastPage, allPages) => {
      const list =
        extractRawListFromResponse(lastPage as Record<string, unknown>) ?? [];
      if (list.length === 0) return undefined;
      const total = readTotalCountFromResponse(
        lastPage as Record<string, unknown>,
      );
      const loadedSoFar = allPages.reduce(
        (sum, page) =>
          sum +
          (extractRawListFromResponse(page as Record<string, unknown>)
            ?.length ?? 0),
        0,
      );
      if (typeof total === "number" && loadedSoFar >= total) return undefined;
      if (list.length < DOUBTS_PAGE_SIZE) return undefined;
      return allPages.length + 1;
    },
    enabled: !!userId,
  });

  const {
    data: bookmarksInfiniteData,
    isPending: isBookmarksPending,
    isFetching: isBookmarksFetching,
    isError: isBookmarksError,
    isFetchingNextPage: isFetchingNextBookmarks,
    hasNextPage: hasNextBookmarksPage,
    fetchNextPage: fetchNextBookmarks,
  } = useInfiniteQuery({
    queryKey: [
      "getDc1UsersUseridBookmarksv2",
      "infinite",
      userId,
      statusFilters,
      bookmarksRefreshTick,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      if (!userId) {
        return Promise.resolve({} as Record<string, unknown>);
      }
      return getDc1UsersUseridBookmarksv2(
        userId,
        { page: pageParam, status: statusFilters },
        { clientName: "doubtsAi" },
      ) as Promise<Record<string, unknown>>;
    },
    getNextPageParam: (lastPage, allPages) => {
      const list =
        extractRawListFromResponse(lastPage as Record<string, unknown>) ?? [];
      if (list.length === 0) return undefined;
      const total = readTotalCountFromResponse(
        lastPage as Record<string, unknown>,
      );
      const loadedSoFar = allPages.reduce(
        (sum, page) =>
          sum +
          (extractRawListFromResponse(page as Record<string, unknown>)
            ?.length ?? 0),
        0,
      );
      if (typeof total === "number" && loadedSoFar >= total) return undefined;
      if (list.length < DOUBTS_PAGE_SIZE) return undefined;
      return allPages.length + 1;
    },
    enabled: showBookmarks && !!userId,
  });

  const doubtsCombinedRawList = React.useMemo(() => {
    const pages = (doubtsInfiniteData?.pages ?? []) as Record<
      string,
      unknown
    >[];
    const all: ReturnType<typeof extractRawListFromResponse> = [];
    for (const p of pages) {
      const list = extractRawListFromResponse(p);
      if (list) all.push(...list);
    }
    return all;
  }, [doubtsInfiniteData]);

  const bookmarksCombinedRawList = React.useMemo(() => {
    const pages = (bookmarksInfiniteData?.pages ?? []) as Record<
      string,
      unknown
    >[];
    const all: ReturnType<typeof extractRawListFromResponse> = [];
    for (const p of pages) {
      const list = extractRawListFromResponse(p);
      if (list) all.push(...list);
    }
    return all;
  }, [bookmarksInfiniteData]);

  const refreshQuestions = React.useCallback(() => {
    setDoubtsRefreshTick((t) => t + 1);
  }, []);

  const queryClient = useQueryClient();

  const refreshDoubtsListAfterCreate = React.useCallback(async () => {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 4000);
    });
    await queryClient.refetchQueries({
      queryKey: ["postDc1UserGetDoubtsv2"],
      type: "active",
      exact: false,
    });
  }, [queryClient]);

  const doubtsErrorToastedRef = React.useRef(false);
  const bookmarksErrorToastedRef = React.useRef(false);

  React.useEffect(() => {
    if (isDoubtsError && !doubtsErrorToastedRef.current) {
      doubtsErrorToastedRef.current = true;
      showSomethingWentWrong();
    } else if (!isDoubtsError) {
      doubtsErrorToastedRef.current = false;
    }
  }, [isDoubtsError, showSomethingWentWrong]);

  React.useEffect(() => {
    if (isBookmarksError && !bookmarksErrorToastedRef.current) {
      bookmarksErrorToastedRef.current = true;
      showSomethingWentWrong();
    } else if (!isBookmarksError) {
      bookmarksErrorToastedRef.current = false;
    }
  }, [isBookmarksError, showSomethingWentWrong]);

  const { mutateAsync: addBookmark } = usePostDc1DoubtBookmark({
    request: { clientName: "doubtsAi" },
  });
  const { mutateAsync: removeBookmark } = useDeleteDc1DoubtBookmark({
    request: { clientName: "doubtsAi" },
  });

  React.useEffect(() => {
    setBookmarkDisplayByDoubtId({});
  }, [userId]);

  const apiQuestions = React.useMemo<QuestionItemProps[]>(() => {
    if (!doubtsCombinedRawList) return [];
    const mapped = mapRawListToQuestionItems(doubtsCombinedRawList, {
      studentTimeZone: session.studentTimeZone,
    });
    const seen = new Set<string>();
    return mapped.filter((q) => {
      const key = q.doubtId != null ? `d:${q.doubtId}` : `i:${q.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [doubtsCombinedRawList, session.studentTimeZone]);

  const serverBookmarkedIdMap = React.useMemo<Record<number, boolean>>(() => {
    const next: Record<number, boolean> = {};
    if (!bookmarksCombinedRawList) return next;
    for (const item of bookmarksCombinedRawList) {
      const rawDoubtId = item.doubtId ?? item.questionId ?? item.question_id;
      const parsedDoubtId = Number(rawDoubtId);
      if (Number.isFinite(parsedDoubtId) && parsedDoubtId > 0) {
        next[parsedDoubtId] = true;
      }
    }
    return next;
  }, [bookmarksCombinedRawList]);

  const effectiveBookmarkedIds = React.useMemo(() => {
    return {
      ...serverBookmarkedIdMap,
      ...bookmarkDisplayByDoubtId,
      ...bookmarkOverridesByDoubtId,
    };
  }, [
    bookmarkDisplayByDoubtId,
    bookmarkOverridesByDoubtId,
    serverBookmarkedIdMap,
  ]);

  const bookmarkedQuestionsFromApi = React.useMemo<QuestionItemProps[]>(() => {
    if (!bookmarksCombinedRawList) return [];
    const mapped = mapRawListToQuestionItems(bookmarksCombinedRawList, {
      forceBookmarked: true,
      studentTimeZone: session.studentTimeZone,
    });
    const seen = new Set<number>();
    return mapped.filter((q) => {
      if (q.doubtId == null) return false;
      if (seen.has(q.doubtId)) return false;
      seen.add(q.doubtId);
      return true;
    });
  }, [bookmarksCombinedRawList, session.studentTimeZone]);

  const filteredBookmarkedQuestions = React.useMemo<QuestionItemProps[]>(() => {
    const byDoubtId = new Map<number, QuestionItemProps>();

    for (const question of apiQuestions) {
      const did = question.doubtId;
      if (did != null && effectiveBookmarkedIds[did] === true) {
        byDoubtId.set(did, question);
      }
    }

    for (const question of bookmarkedQuestionsFromApi) {
      const did = question.doubtId;
      if (did == null) continue;
      if (effectiveBookmarkedIds[did] !== true) continue;
      if (!byDoubtId.has(did)) {
        byDoubtId.set(did, question);
      }
    }

    return Array.from(byDoubtId.values());
  }, [apiQuestions, bookmarkedQuestionsFromApi, effectiveBookmarkedIds]);

  const mergeBookmarkState = React.useCallback(
    (q: QuestionItemProps): QuestionItemProps => {
      const did = q.doubtId;
      if (did != null && effectiveBookmarkedIds[did] !== undefined) {
        return { ...q, isBookmarked: Boolean(effectiveBookmarkedIds[did]) };
      }
      return q;
    },
    [effectiveBookmarkedIds],
  );

  const mergeResolvedStatusOverride = React.useCallback(
    (q: QuestionItemProps): QuestionItemProps => {
      const did = q.doubtId;
      if (did == null) return q;
      const override = statusOverridesByDoubtId[did];
      return override ? { ...q, ...override } : q;
    },
    [statusOverridesByDoubtId],
  );

  const questionsForMainList = React.useMemo(
    () => apiQuestions.map(mergeBookmarkState).map(mergeResolvedStatusOverride),
    [apiQuestions, mergeBookmarkState, mergeResolvedStatusOverride],
  );

  const questionsForBookmarkPanel = React.useMemo(
    () =>
      filteredBookmarkedQuestions
        .map(mergeBookmarkState)
        .map(mergeResolvedStatusOverride),
    [
      filteredBookmarkedQuestions,
      mergeBookmarkState,
      mergeResolvedStatusOverride,
    ],
  );

  const handleDoubtResolvedStatusUpdate = React.useCallback(
    (doubtId: number) => {
      if (!Number.isFinite(doubtId) || doubtId <= 0) return;
      setStatusOverridesByDoubtId((prev) => ({
        ...prev,
        [doubtId]: {
          status: "RESOLVED",
          doubtCardStatus: "Solved",
        },
      }));
    },
    [],
  );

  const handleBookmarkToggle = React.useCallback(
    async (question: QuestionItemProps) => {
      const doubtId = question.doubtId;
      if (!doubtId) {
        return;
      }

      const currentlyBookmarked =
        mergeBookmarkState(question).isBookmarked === true;

      setBookmarkOverridesByDoubtId((prev) => ({
        ...prev,
        [doubtId]: !currentlyBookmarked,
      }));

      try {
        const payload = {
          doubtId,
          userId,
          source: "web" as const,
        };
        const response = (await (currentlyBookmarked
          ? removeBookmark({ data: payload })
          : addBookmark({ data: payload }))) as { success?: boolean };

        if (response?.success !== true) {
          setBookmarkOverridesByDoubtId((prev) => {
            const next = { ...prev };
            delete next[doubtId];
            return next;
          });
          return;
        }

        setBookmarkDisplayByDoubtId((prev) => ({
          ...prev,
          [doubtId]: !currentlyBookmarked,
        }));
        track(AnalyticsEvents.DOUBTS_BOOKMARK_TOGGLED, 1, {
          action: currentlyBookmarked ? "removed" : "added",
        });
        setBookmarkOverridesByDoubtId((prev) => {
          const next = { ...prev };
          delete next[doubtId];
          return next;
        });
      } catch (e) {
        setBookmarkOverridesByDoubtId((prev) => {
          const next = { ...prev };
          delete next[doubtId];
          return next;
        });
        showSomethingWentWrong(e);
      }
    },
    [
      addBookmark,
      mergeBookmarkState,
      removeBookmark,
      showSomethingWentWrong,
      track,
      userId,
    ],
  );

  /** Only show full-panel loading when there is no cached list yet (not on refetch / background bookmark sync). */
  const doubtsListIsInitialLoading =
    !showBookmarks && isDoubtsPending && doubtsCombinedRawList.length === 0;
  const bookmarksListIsInitialLoading =
    showBookmarks &&
    (isBookmarksPending || isBookmarksFetching) &&
    bookmarksCombinedRawList.length === 0;

  const showFilterEmptyIllustration =
    !showBookmarks &&
    !doubtsListIsInitialLoading &&
    !isDoubtsError &&
    questionsForMainList.length === 0 &&
    isDoubtsFilterActive(appliedFilter);

  const leftPanelQuestions = showBookmarks
    ? questionsForBookmarkPanel
    : questionsForMainList;

  const leftPanelLoading = showBookmarks
    ? bookmarksListIsInitialLoading
    : doubtsListIsInitialLoading;

  const showBookmarksEmpty =
    showBookmarks &&
    !leftPanelLoading &&
    !isBookmarksError &&
    questionsForBookmarkPanel.length === 0;
  const leftPanelError = showBookmarks ? isBookmarksError : isDoubtsError;
  const leftPanelRetry = showBookmarks
    ? () => setBookmarksRefreshTick((t) => t + 1)
    : refreshQuestions;

  const isNoDoubtsYetEmpty =
    !leftPanelLoading &&
    !leftPanelError &&
    !showFilterEmptyIllustration &&
    !showBookmarksEmpty &&
    leftPanelQuestions.length === 0;

  const dashContentRef = React.useRef<HTMLDivElement>(null);
  const [emptyPanelHeight, setEmptyPanelHeight] = React.useState<
    number | undefined
  >(undefined);

  React.useLayoutEffect(() => {
    if (!isNoDoubtsYetEmpty || isMweb) {
      setEmptyPanelHeight(undefined);
      return;
    }

    const measure = () => {
      const root = dashContentRef.current;
      if (!root) return;
      const fileUpload = root.querySelector(".file-upload-container");
      if (!fileUpload) return;
      const rootRect = root.getBoundingClientRect();
      const fuRect = fileUpload.getBoundingClientRect();
      setEmptyPanelHeight(fuRect.bottom - rootRect.top + 35);
    };

    measure();

    const ro = new ResizeObserver(measure);
    if (dashContentRef.current) ro.observe(dashContentRef.current);
    return () => ro.disconnect();
  }, [isNoDoubtsYetEmpty, isMweb]);

  const handleFilterApply = React.useCallback(
    (state: DoubtsFilterState) => {
      setAppliedFilter(state);
      track(AnalyticsEvents.DOUBTS_FILTER_APPLIED, 1, {
        has_subject_filter: state.subjects.length > 0,
        has_status_filter: state.status.length > 0,
        has_date_range: Boolean(state.startDate || state.endDate),
      });
    },
    [track],
  );

  const handleDismissDoubtsFilters = React.useCallback(
    (options: { resetApplied: boolean }) => {
      setIsFilterOpen(false);
      if (options.resetApplied) {
        setAppliedFilter(emptyDoubtsFilterState());
      }
    },
    [],
  );

  React.useEffect(() => {
    track(AnalyticsEvents.DOUBTS_SCREEN_VIEWED, 1, {
      screen_name: "doubts",
      surface: "web",
      layout: isNarrowLayout ? "mobile" : "desktop",
    });
    // Once per mount; layout at first paint (avoid duplicate on resize).
  }, []);

  const openExistingQuestionChatRef = React.useRef<
    ((q: QuestionItemProps) => void) | null
  >(null);

  const appliedFilterCount = React.useMemo(() => {
    let c = 0;
    if (appliedFilter.subjects.length > 0) c += 1;
    if (appliedFilter.status.length > 0) c += 1;
    if (appliedFilter.startDate || appliedFilter.endDate) c += 1;
    return c;
  }, [
    appliedFilter.subjects,
    appliedFilter.status,
    appliedFilter.startDate,
    appliedFilter.endDate,
  ]);
  const showMwebQuestionsSheet =
    showBookmarks ||
    isDoubtsFilterActive(appliedFilter) ||
    questionsForMainList.length > 0 ||
    leftPanelError;

  const showMwebGreeting =
    !accessExhausted &&
    !showBookmarks &&
    questionsForMainList.length === 0 &&
    !isDoubtsFilterActive(appliedFilter) &&
    !isDoubtsError;

  // --- Infinite scroll: trigger next page when the bottom sentinel becomes visible. ---
  const loadMoreSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const activeHasNextPage = showBookmarks
    ? hasNextBookmarksPage
    : hasNextDoubtsPage;
  const activeIsFetchingNext = showBookmarks
    ? isFetchingNextBookmarks
    : isFetchingNextDoubts;
  const activeFetchNext = React.useCallback(() => {
    if (showBookmarks) {
      void fetchNextBookmarks();
    } else {
      void fetchNextDoubts();
    }
  }, [fetchNextBookmarks, fetchNextDoubts, showBookmarks]);

  React.useEffect(() => {
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    if (!activeHasNextPage) return;
    if (activeIsFetchingNext) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        activeFetchNext();
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [
    activeFetchNext,
    activeHasNextPage,
    activeIsFetchingNext,
    showBookmarks,
    leftPanelQuestions.length,
  ]);

  const filterModal = isFilterOpen ? (
    <Filter
      initialValue={appliedFilter}
      timeZone={session.studentTimeZone}
      onApply={handleFilterApply}
      onClose={() => setIsFilterOpen(false)}
    />
  ) : null;

  const leftPanelEl = (
    <LeftPanel
      questionsForList={leftPanelQuestions}
      showFilterEmptyIllustration={showFilterEmptyIllustration}
      showBookmarksEmpty={showBookmarksEmpty}
      isLoading={leftPanelLoading}
      isError={leftPanelError}
      onRetry={leftPanelRetry}
      showBookmarks={showBookmarks}
      onToggleBookmarks={() =>
        setShowBookmarks((v) => {
          const next = !v;
          track(AnalyticsEvents.DOUBTS_BOOKMARKS_MODE_CHANGED, 1, {
            mode: next ? "bookmarks" : "all",
          });
          return next;
        })
      }
      onOpenFilter={() => {
        track(AnalyticsEvents.DOUBTS_FILTER_OPENED, 1, {});
        setIsFilterOpen(true);
      }}
      onBookmarkToggle={(q) => void handleBookmarkToggle(q)}
      onQuestionPress={(q) => openExistingQuestionChatRef.current?.(q)}
      variant={isMweb ? "mweb" : "desktop"}
      appliedFilterCount={appliedFilterCount}
      highlightedDoubtId={activeChatDoubtId}
      emptyStateHeight={isNoDoubtsYetEmpty ? emptyPanelHeight : undefined}
      loadMoreSentinelRef={loadMoreSentinelRef}
      isFetchingMore={activeIsFetchingNext}
      hasMore={activeHasNextPage}
    />
  );

  if (isMweb) {
    return (
      <VStack
        className={`container doubts-mweb-layout ${accessExhausted ? "doubts-mweb-layout--exhausted" : ""}`}
      >
        <div className="doubts-mweb-root">
          <div className="doubts-mweb-gradient">
            <div className="doubts-mweb-gradient-overlay" aria-hidden />
            <div className="doubts-mweb-header-wrap">
              <Header
                title={t("page_title")}
                titleStyles={{
                  ...theme.typography.title.large.regular,
                  color: theme.colors.text[50],
                  fontStyle: "normal",
                  fontWeight: 500,
                  textAlign: "center",
                }}
                titleAlign="center"
                onMenuPress={() => {}}
                navigate={navigate}
                handleLogOut={() => {}}
                showRight={false}
                backIconShadow
                sectionLeftStyles={{ marginLeft: spacingRem(theme.spacing.sm) }}
              />
            </div>
            <div className="doubts-mweb-body-scroll">
              <AskQuestionPanel
                layout="mweb"
                accessExhausted={accessExhausted}
                showMwebGreeting={showMwebGreeting}
                mwebListSlot={
                  showMwebQuestionsSheet ? (
                    <div className="doubts-mweb-questions-sheet">
                      {leftPanelEl}
                    </div>
                  ) : null
                }
                onChatSessionClose={refreshQuestions}
                onDoubtsListShouldRefresh={refreshDoubtsListAfterCreate}
                onActiveChatDoubtIdChange={setActiveChatDoubtId}
                onDismissDoubtsFilters={handleDismissDoubtsFilters}
                openExistingQuestionChatRef={openExistingQuestionChatRef}
              />
            </div>
          </div>
        </div>
        {filterModal}
      </VStack>
    );
  }

  return (
    <VStack className="container">
      <Header
        showCenter={false}
        left={
          <Text as="span" style={theme.typography.headline.medium.emphasized}>
            {t("page_title")}
          </Text>
        }
        rightIconItems={AVATAR_ITEMS}
        rightIcon="userProfileIcon"
        onRightPress={() => {}}
      />
      <Box
        ref={dashContentRef}
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <HStack
          className="dashboard-content"
          gap="l"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            gap: spacingRem(theme.spacing.md),
          }}
        >
          {leftPanelEl}
          <RightPanel>
            <AskQuestionPanel
              accessExhausted={accessExhausted}
              showMwebGreeting={showMwebGreeting}
              onChatSessionClose={refreshQuestions}
              onDoubtsListShouldRefresh={refreshDoubtsListAfterCreate}
              onActiveChatDoubtIdChange={setActiveChatDoubtId}
              onDoubtResolved={handleDoubtResolvedStatusUpdate}
              onDismissDoubtsFilters={handleDismissDoubtsFilters}
              openExistingQuestionChatRef={openExistingQuestionChatRef}
            />
          </RightPanel>
        </HStack>
      </Box>
      {filterModal}
    </VStack>
  );
};

export default DoubtsDashboard;
